import React, { useEffect, useMemo } from 'react';
import Camera, { FACING_MODES } from 'react-html5-camera-photo';
import 'react-html5-camera-photo/build/css/index.css';
import { useTranslation } from 'react-i18next';
import { Dimensions, Modal, Platform, StatusBar, StyleSheet, View } from 'react-native';
import Tooltip from 'react-native-walkthrough-tooltip';
import { SCREEN_HEIGHT, SCREEN_WIDTH, widthPercentageToDP as wp } from '../../utils/screensize';
import useCameraStream from '../../hooks/useCameraStream';
import Button from '../global/Button';
import Text from '../global/Text';
import AddFile from './AddFile';
import AddPhoto from './AddPhoto';
import QuestionnaireAttachmentButton from './QuestionnaireAttachmentButton';
import AddVideo from './AddVideo';
import { QuestionnaireSwiperContainerProps } from './QuestionnaireSwiperContainer';

const PhotoIcon = require('../../assets/icons/take-photo-disable.png');
const FileIcon = require('../../assets/icons/take-file-disable.png');
const VideoIcon = require('../../assets/icons/take-video-disable.png');

const screen = Dimensions.get('screen');

export type QuestionnaireSwiperMediaProps = Pick<
  QuestionnaireSwiperContainerProps,
  | 'language'
  | 'question'
  | 'index'
  | 'editingPhotoIndex'
  | 'handleConfirmEditPhoto'
  | 'handleNavRecordVideo'
  | 'handleNavPlaybackVideo'
  | 'handleActivateImagePicker'
  | 'hasVideoAttachment'
  | 'isTutorialMode'
  | 'currentQuestionIndex'
  | 'isShowQuestionTutorialModal'
  | 'currentConditionIndex'
  | 'handleEditUploadFile'
  | 'handleUploadDocuments'
  | 'showCamera'
  | 'handleTakePhoto'
  | 'handleCloseCamera'
>;

const QuestionnaireSwiperMedia = (props: QuestionnaireSwiperMediaProps) => {
  const { t } = useTranslation(['questionnaire', 'common']);
  const [isShowTooltip, setIsShowTooltip] = React.useState(false);
  const [cameraFacingMode, setCameraFacingMode] = React.useState(FACING_MODES.ENVIRONMENT);
  const [cameraError, setCameraError] = React.useState<Error | null>(null);
  const [hasTriedUserFacingFallback, setHasTriedUserFacingFallback] = React.useState(false);
  const { handleCameraStart } = useCameraStream(props.showCamera);
  const {
    question: { answer: questionnaireAnswer }
  } = props;

  useEffect(() => {
    if (!props.isShowQuestionTutorialModal && props.isTutorialMode && props.index === props.currentQuestionIndex) {
      setIsShowTooltip(true);
    } else if (
      props.isShowQuestionTutorialModal &&
      props.isTutorialMode &&
      props.index === props.currentQuestionIndex
    ) {
      setIsShowTooltip(false);
    }
  }, [props.isShowQuestionTutorialModal, props.isTutorialMode, props.index, props.currentQuestionIndex]);

  useEffect(() => {
    if (!props.showCamera) {
      setCameraFacingMode(FACING_MODES.ENVIRONMENT);
      setCameraError(null);
      setHasTriedUserFacingFallback(false);
    }
  }, [props.showCamera]);

  const isNotApplicable = useMemo(() => questionnaireAnswer === 'not-applicable', [questionnaireAnswer]);

  const normalizeCameraError = (error: unknown): Error => {
    if (error instanceof Error) return error;
    if (typeof error === 'string') return new Error(error);
    return new Error('Unable to access camera.');
  };

  const handleCameraError = (error: unknown) => {
    const normalizedError = normalizeCameraError(error);
    const errorName = normalizedError.name?.toLowerCase() || '';
    const errorMessage = normalizedError.message?.toLowerCase() || '';
    const shouldFallbackToFrontCamera =
      cameraFacingMode === FACING_MODES.ENVIRONMENT &&
      !hasTriedUserFacingFallback &&
      (errorName.includes('notfound') ||
        errorName.includes('overconstrained') ||
        errorMessage.includes('requested device not found') ||
        errorMessage.includes('could not start video source'));

    if (shouldFallbackToFrontCamera) {
      setHasTriedUserFacingFallback(true);
      setCameraError(null);
      setCameraFacingMode(FACING_MODES.USER);
      return;
    }

    setCameraError(normalizedError);
  };

  const handleRetryCamera = () => {
    setCameraError(null);
    setHasTriedUserFacingFallback(false);
    setCameraFacingMode(FACING_MODES.ENVIRONMENT);
  };

  const getCameraErrorMessage = () => {
    if (!cameraError) return '';

    const errorName = cameraError.name?.toLowerCase() || '';
    const errorMessage = cameraError.message || 'Unable to access camera.';

    if (errorName.includes('notallowed') || errorName.includes('permission')) {
      return 'Camera access was blocked. Please allow camera permission in your browser and try again.';
    }

    if (hasTriedUserFacingFallback) {
      return 'We could not find a compatible rear or front camera on this device/browser.';
    }

    return errorMessage;
  };

  const isUsingFrontCameraFallback =
    hasTriedUserFacingFallback && cameraFacingMode === FACING_MODES.USER && !cameraError;
  /**
   * conditions: {
   *    'key': Question{
   *       ...,
   *       photos:
   *    }
   * }
   */
  if (!props.question.photoLimit && !props.question.videoLimit && !props.question.documentLimit) {
    return null;
  }

  return (
    <View style={styles.photosContainer}>
      <View style={styles.photosText}>
        <Text>{t('questionnaire:swiperMedia.selectMedia')}</Text>
        <Text style={styles.attachMinimum}>
          {props.question.photoMinimum > 0 && !isNotApplicable
            ? `(${t('questionnaire:swiperMedia.minimumPhoto')}: ${props.question.photoMinimum})`
            : null}{' '}
          {props.question.videoLimit > 0 && props.hasVideoAttachment && !isNotApplicable
            ? `(${t('questionnaire:swiperMedia.minimumVideo')}: 1)`
            : null}
        </Text>
      </View>
      <View style={styles.DocumentText}>
        {props.question.documentMinimum > 0 && !isNotApplicable ? (
          <Text style={styles.photoMinimum}>
            ({t('questionnaire:swiperMedia.minimumDocuments')}: {props.question.documentMinimum})
          </Text>
        ) : null}
      </View>

      <View style={styles.photos} testID={'media-container'}>
        {props.question.photoLimit > 0 &&
          props.question?.downloadedImage &&
          props.question?.downloadedImage.map((photo, photoIndex) =>
            photo ? (
              <AddPhoto
                key={`question-${props.index}_photo-${photoIndex + 1}`}
                imageLink={photo}
                photoIndex={photoIndex}
                newPhoto={false}
                isValid={true}
                language={props.language}
                isEditing={props.editingPhotoIndex === photoIndex}
                onActivateEdit={() => props.handleConfirmEditPhoto(photoIndex, undefined, props.currentConditionIndex)}
              />
            ) : (
              ''
            )
          )}

        {props.hasVideoAttachment &&
          props.question.videoLimit > 0 &&
          props.question.videos &&
          props.question.videos.map((video: string, videoIndex: number) => (
            <AddVideo
              key={`question-${props.index}_photo-${videoIndex + 1}`}
              source={video ? video : ''}
              language={props.language}
              isValid={isNotApplicable || props.question.videos?.length >= 1}
              onRecordVideo={videoProps => props.handleNavRecordVideo(props.currentConditionIndex!, videoProps)}
              onPlaybackVideo={uri => props.handleNavPlaybackVideo(uri, videoIndex, props.currentConditionIndex)}
            />
          ))}
      </View>

      <View style={styles.documentSection}>
        {props.question &&
          props.question.documents &&
          props.question.documents.map((file: string, fileIndex: number) => (
            <AddFile
              key={`question-${props.index}_document-${fileIndex + 1}`}
              source={file}
              language={props.language}
              isValid={true}
              onUploadFile={videoProps => {
                props.handleUploadDocuments(props.currentConditionIndex, videoProps);
              }}
              onEditFile={() => props.handleEditUploadFile(fileIndex, props.currentConditionIndex)}
            />
          ))}
      </View>

      {/* <View style={styles.attachSection}>
        <Text>{t('questionnaire:swiperMedia.selectMedia')}</Text>
      </View> */}

      <View style={styles.containerAttachment} testID={'media-container'}>
        {props.question.photoLimit > 0 &&
        (!props.question.photos || props.question.photos.filter(photo => photo).length < props.question.photoLimit) ? (
          <Tooltip
            isVisible={isShowTooltip}
            topAdjustment={Platform.OS === 'android' ? -StatusBar.currentHeight! : 0}
            placement="top"
            content={<Text>{t('questionnaire:tutorial.clickIcon')}</Text>}
            onClose={() => setIsShowTooltip(false)}
          >
            <AddPhoto
              newPhoto={true}
              language={props.language}
              isTutorialMode={props.isTutorialMode}
              isValid={
                isNotApplicable ||
                props.question.photoMinimum == null ||
                props.question.photoMinimum === 0 ||
                (props.question.photoMinimum > 0 &&
                  props.question.photos &&
                  props.question.photos.length >= props.question.photoMinimum)
              }
              onActivateImagePicker={imageProps => {
                return props.handleActivateImagePicker(props.currentConditionIndex, imageProps);
              }}
            />
          </Tooltip>
        ) : props.question.photoLimit ? (
          <QuestionnaireAttachmentButton disabled iconSource={PhotoIcon.default} label={t('questionnaire:addPhoto')} />
        ) : null}

        {props.hasVideoAttachment &&
        props.question.videoLimit > 0 &&
        (!props.question.videos || props.question.videos.filter(video => video).length < props.question.videoLimit) ? (
          <AddVideo
            source={''}
            language={props.language}
            isValid={
              isNotApplicable ||
              props.question.videoLimit == null ||
              props.question.videoLimit === 0 ||
              (props.question.videoLimit > 0 &&
                props.question.videos &&
                props.question.videos.length >= props.question.videoLimit)
            }
            onRecordVideo={videoProps => {
              props.handleNavRecordVideo(props.currentConditionIndex, videoProps);
            }}
            onPlaybackVideo={() => null}
          />
        ) : props.question.videoLimit && props.hasVideoAttachment ? (
          <QuestionnaireAttachmentButton disabled iconSource={VideoIcon.default} label={t('questionnaire:addVideo')} />
        ) : null}

        {props?.question?.documentLimit > 0 &&
        (!props?.question?.documents ||
          props?.question?.documents.filter(video => video).length < props.question?.documentLimit) ? (
          <AddFile
            source={''}
            language={props.language}
            isValid={
              isNotApplicable ||
              props.question?.documentMinimum == null ||
              props.question?.documentMinimum === 0 ||
              (props.question?.documentMinimum > 0 &&
                props.question?.documents &&
                props.question?.documents.length >= props.question?.documentMinimum)
            }
            onUploadFile={videoProps => {
              props.handleUploadDocuments(props.currentConditionIndex, videoProps);
            }}
          />
        ) : props.question.documentLimit ? (
          <QuestionnaireAttachmentButton disabled iconSource={FileIcon.default} label={t('questionnaire:takeFile')} />
        ) : null}

        <Modal visible={props.showCamera}>
          <View style={styles.cameraModalContent}>
            <View style={styles.cameraViewport}>
              {props.showCamera && !cameraError ? (
                <Camera
                  key={cameraFacingMode}
                  isImageMirror={cameraFacingMode === FACING_MODES.USER}
                  isDisplayStartCameraError={false}
                  idealFacingMode={cameraFacingMode}
                  onCameraStart={stream => {
                    handleCameraStart(stream);
                    setCameraError(null);
                  }}
                  onCameraError={handleCameraError}
                  onTakePhoto={dataUri => {
                    props.handleTakePhoto(props?.currentConditionIndex, dataUri);
                  }}
                />
              ) : null}

              {props.showCamera && cameraError ? (
                <View style={styles.cameraErrorContainer}>
                  <Text style={styles.cameraErrorTitle}>Camera unavailable</Text>
                  <Text style={styles.cameraErrorMessage}>{getCameraErrorMessage()}</Text>
                  <Button onPress={handleRetryCamera} style={styles.retryButton} title={'Try again'} />
                </View>
              ) : null}
            </View>

            {isUsingFrontCameraFallback ? (
              <Text style={styles.cameraHintText}>Rear camera was not found. Switched to the front camera.</Text>
            ) : null}

            <View style={[styles.buttonContainer]}>
              <Button onPress={props.handleCloseCamera} style={styles.button} title={t('common:cancel')} />
            </View>
          </View>
        </Modal>
      </View>
    </View>
  );
};

export default QuestionnaireSwiperMedia;

const styles = StyleSheet.create({
  photosContainer: {
    marginTop: 10,
    alignItems: 'center'
    // flexWrap: 'wrap'
  },
  subtitle: {
    textAlign: 'center',
    fontSize: SCREEN_WIDTH > 500 ? wp(0.8) : wp(3),
    color: '#C4C4C4'
  },
  portraitBox: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 100,
    height: (62 / 3) * 4 + 4,
    marginVertical: 10,
    marginHorizontal: 2,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: '#FAFAFA',
    backgroundColor: '#FAFAFA'
  },
  portraitBoxVideo: {
    width: SCREEN_WIDTH - 80,
    height: (62 / 3) * 1 + 4,
    marginVertical: 10,
    marginHorizontal: 2,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: '#FAFAFA',
    backgroundColor: '#FAFAFA'
  },
  attachSection: {
    flexDirection: 'column',
    marginTop: 25
  },
  DocumentText: {
    flexDirection: 'column',
    marginBottom: 20
  },
  cameraModalContent: {
    flex: 1,
    backgroundColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: SCREEN_HEIGHT * 0.1 + 24
  },
  cameraViewport: {
    width: SCREEN_WIDTH > 500 ? 768 : SCREEN_WIDTH,
    maxWidth: 768,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000',
    overflow: 'hidden'
  },
  cameraErrorContainer: {
    width: '100%',
    minHeight: 280,
    paddingHorizontal: 24,
    paddingVertical: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#111827'
  },
  cameraErrorTitle: {
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12
  },
  cameraErrorMessage: {
    color: '#E5E7EB',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 12,
    maxWidth: 520
  },
  cameraHintText: {
    color: '#E5E7EB',
    textAlign: 'center',
    marginTop: 16,
    paddingHorizontal: 24,
    maxWidth: 768
  },
  button: {
    width: 400,
    marginHorizontal: 'auto',
    marginTop: 15
  },
  retryButton: {
    width: 240,
    marginHorizontal: 'auto',
    marginTop: 8
  },
  buttonContainer: {
    minHeight: 64,
    height: SCREEN_HEIGHT * 0.1,
    width: SCREEN_WIDTH > 500 ? 768 : SCREEN_WIDTH,
    marginHorizontal: 'auto',
    paddingVertical: 12,
    backgroundColor: '#535353',
    zIndex: 2,
    bottom: 0,
    position: 'absolute',
    marginTop: -5
  },
  photosText: {
    flexDirection: 'column'
  },
  documentSection: {
    marginTop: 8,
    width: '100%',
    alignItems: 'center'
  },
  containerAttachment: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginTop: 4
  },
  photoMinimum: {
    textAlign: 'center'
  },
  icon: {
    marginBottom: SCREEN_WIDTH > 500 ? wp(0.1) : wp(2),
    height: SCREEN_WIDTH > 500 ? wp(1.8) : wp(8),
    width: SCREEN_WIDTH > 500 ? wp(1.8) : wp(8)
  },
  photos: {
    // width,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginHorizontal: 20
  },
  attachMinimum: {
    textAlign: 'center'
  }
});
